import type { UnwrapPromise } from "@prisma/client";
import { LoaderFunction, useLoaderData } from "remix";
import { Booth } from "~/components/Booth";
import { ButtonLink } from "~/components/Button";
import { ContentBlock } from "~/components/ContentBlock";
import { ContentBlockModel, getContentBlock } from "~/utils/contentBlocks";
import { db } from "~/utils/db.server";
import { requireFestival } from "~/utils/festival.server";
import { getUserId } from "~/utils/session.server";
import { useIsLoggedIn } from "~/utils/useIsLoggedIn";

interface LoaderData {
  title: ContentBlockModel;
  content: ContentBlockModel;
  editable: boolean;
  booths: UnwrapPromise<ReturnType<typeof loadBooths>>;
}

function loadBooths(festivalId: string) {
  return db.booth.findMany({
    where: { festivalId },
    orderBy: {
      title: "asc",
    },
    include: { image: true },
  });
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const [festival, title, content, userId] = await Promise.all([
    requireFestival(params.festivalSlug),
    getContentBlock(`digitale-staende-${params.festivalSlug}/title`),
    getContentBlock(`digitale-staende-${params.festivalSlug}/content`),
    getUserId(request),
  ]);

  const booths = await loadBooths(festival.id);

  return {
    title,
    content,
    editable: userId !== null,
    booths,
  };
};

export default function DigitaleStaendeIndex() {
  const { title, content, editable, booths } = useLoaderData<LoaderData>();
  const isLoggedIn = useIsLoggedIn();

  return (
    <>
      <div className="my-10">
        <h1 className="text-5xl font-bold mb-3">
          <ContentBlock block={title} editable={editable} />
        </h1>

        <ContentBlock block={content} editable={editable} />
      </div>

      <div className="main-layout-full-bleed bg-gray-50 py-10 border-t border-t-gray-200">
        <div className="main-layout-centered">
          <ul className="py-2 list-reset grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {booths.map((booth) => (
              <li key={booth.id} className="w-full">
                <Booth
                  name={booth.title}
                  short={booth.summary}
                  image={booth.image}
                  linkProps={{ to: booth.slug }}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      {isLoggedIn && (
        <div className="my-4">
          <ButtonLink to="new">Neuen Stand Hinzufügen</ButtonLink>
        </div>
      )}
    </>
  );
}
